import React from 'react';
import { colors } from 'src/sass/common/_variables';
import { createTheme } from '@mui/material';

export type BadgeType =
  | 'error'
  | 'success'
  | 'warning'
  | 'muted'
  | 'basic'
  | 'white'
  | 'primary'
  | 'primary-light'
  | 'blue'
  | undefined;
export type BadgeProps = {
  content?: React.ReactNode | string;
  type?: BadgeType;
  icon?: any;
  endIcon?: any;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement> | (() => void) | undefined;
  onIconClick?: React.MouseEventHandler<HTMLDivElement> | (() => void) | undefined;
  onEndIconClick?: React.MouseEventHandler<HTMLDivElement> | (() => void) | undefined;
  className?: string;
  iconRight?: boolean;
  version?: 'v1' | 'v2';
  id?: string;
  deleteFilter?: (
    value: string,
    filter: any,
    check: string,
    nested: boolean,
    isEmpByDept: boolean,
  ) => void;
  filter?: any;
  textSize?: string;
} & React.ComponentPropsWithRef<'span'>;

export const icpFitmentTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$colorWhite,
          boxShadow:
            '0px -10px 15px 2px rgba(0, 0, 0, 0.05), 0px 20px 25px -5px rgba(0, 0, 0, 0.10)',
          minWidth: '301px',
          maxWidth: '394px',
          borderRadius: '8px',
          padding: '0px',
        },
        arrow: {
          color: colors.$colorWhite,
          fontSize: '24px',
          '&::before': {
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
  },
});

export const createTooltipThemeStyles = (
  tooltipProps: Record<string, any> = {},
  arrowProps: Record<string, any> = {},
) => {
  const theme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.$colorWhite,
            borderRadius: '8px',
            ...tooltipProps,
          },
          arrow: {
            color: colors.$colorDarkPrimary150,
            fontSize: '24px',
            ...arrowProps,
          },
        },
      },
    },
  });

  return theme;
};

export const icpFitmentColumnHeaderTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          width: '320px',
          padding: '0px',
        },
        arrow: {
          color: colors.$tooltipBgColor,
        },
      },
    },
  },
});

export const webpagesCustomTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$tooltipBgColor,
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          width: '320px',
          padding: '16px',
        },
        arrow: {
          color: colors.$tooltipBgColor,
        },
      },
    },
  },
});

export const blackTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$tooltipBgColor,
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          padding: '6px 12px',
          color: '#fff !important', // Force white text with high specificity
        },
        arrow: {
          color: colors.$tooltipBgColor,
        },
      },
    },
  },
});

export const CopyTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$colorWhite,
          color: colors.$colorBlack,
          boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.12)',
          borderRadius: '6px',
          position: 'relative',
        },
        arrow: {
          color: colors.$colorWhite,
        },
      },
    },
  },
});

export const AiQuestionToolTipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          color: colors.$colorBlack,
          backgroundColor: colors.$colorWhite,
        },
      },
    },
  },
});

export const HpmTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          color: colors.$colorWhite,
          backgroundColor: colors.$colorGrayBeta800,
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '500',
          lineHeight: '20px',
          padding: '4px 12px',
        },
      },
    },
  },
});

export const disabledLockBlackTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$tooltipBgColor,
          borderStyle: 'none',
          borderRadius: '4px',
          position: 'relative',
          padding: '4px 12px',
          fontWeight: '500',
          bottom: '-10px',
          fontSize: '14px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        },
        arrow: {
          color: colors.$tooltipBgColor,
        },
      },
    },
  },
});

export const headerActionsTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '6px',
          padding: '8px',
          backgroundColor: colors.$colorWhite,
          boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)',
          marginTop: '18px !important',
          marginRight: '0px !important',
          position: 'relative',
          bottom: '14px',
        },
      },
    },
  },
});

export const TALTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '8px',
          padding: '12px',
          backgroundColor: colors.$colorWhite,
          minWidth: '280px',
          boxShadow:
            '0px 20px 25px -5px rgba(0, 0, 0, 0.10), 0px 10px 10px 0px rgba(0, 0, 0, 0.04)',
          marginTop: 0,
          position: 'relative',
          maxWidth: 'fit-content',
        },
      },
    },
  },
});

export const blackTooltipThemeCmpModal = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          width: '200px',
          backgroundColor: colors.$tooltipBgColor,
          borderStyle: 'none',
          fontSize: '14px',
          borderRadius: '4px',
          position: 'relative',
          bottom: '-10px',
          padding: '6px 12px',
          textAlign: 'center',
        },
        arrow: {
          color: colors.$tooltipBgColor,
        },
      },
    },
  },
});

export const ccInfoIconTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$colorBlack,
          color: colors.$colorWhite,
          maxWidth: '383px',
          boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.12)',
          borderWidth: '0px',
          borderStyle: 'none',
          borderRadius: '4px',
          // position: 'relative',
          // top: '-8px',
        },
        arrow: {
          color: colors.$colorBlack,
        },
      },
    },
  },
});

export const hpmBlackTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$colorGrayBeta800,
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          padding: '6px 12px',
          maxWidth: '240px',
          fontSize: '14px',
          textAlign: 'center',
          fontWeight: '500',
        },
        arrow: {
          color: colors.$colorGrayBeta800,
        },
      },
    },
  },
});

export const FilterSummaryTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '8px',
          backgroundColor: colors.$colorWhite,
          minWidth: '520px',
          maxHeight: '548px',
          boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)',
          marginTop: 0,
          position: 'relative',
          maxWidth: '520px',
          padding: '0px',
        },
      },
    },
  },
});

export const SCTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '4px',
          backgroundColor: colors.$colorWhite,
          minWidth: 'auto',
          boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
          marginTop: 0,
          maxWidth: '160px',
        },
        arrow: {
          color: colors.$colorWhite,
          fontSize: '24px',
          '&::before': {
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
  },
});

export const ScTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$tooltipBgColor,
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          padding: '6px 12px',
          maxWidth: '350px',
        },
        arrow: {
          color: colors.$tooltipBgColor,
        },
      },
    },
  },
});

export const cbTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '6px',
          padding: '0px',
          backgroundColor: colors.$colorWhite,
          minWidth: 'auto',
          boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)',
          marginTop: 0,
          maxWidth: '162px',
        },
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -14],
              },
            },
          ],
        },
      },
    },
  },
});

export const cbTooltipThemeAutoWidth = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderStyle: 'none',
          borderRadius: '6px',
          padding: '0px',
          backgroundColor: colors.$colorWhite,
          minWidth: '100px',
          boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)',
          marginTop: 0,
          maxWidth: 'fit-content',
        },
      },
    },
  },
});

export const bulkImportLabelTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          width: '210px',
          height: '50px',
          padding: '0px 12px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: colors.$colorBlack,
          color: colors.$colorWhite,
          fontWeight: 500,
          fontSize: '14px',
          boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
          position: 'relative',
          top: '-2px',
        },
        arrow: {
          color: colors.$colorBlack,
        },
      },
    },
  },
});

export const millerHeimanTooltipTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$colorWhite,
          boxShadow:
            '0px -10px 15px 2px rgba(0, 0, 0, 0.05), 0px 20px 25px -5px rgba(0, 0, 0, 0.10)',
          minWidth: '339px',
          maxWidth: '339px',
          borderRadius: '8px',
          padding: '0px',
        },
        arrow: {
          color: colors.$colorWhite,
          fontSize: '24px',
          '&::before': {
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
  },
});


export const blackTooltipThemeICPRange = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.$tooltipBgColor,
          borderStyle: 'none',
          borderRadius: '6px',
          position: 'relative',
          padding: '6px 12px',
          color: '#fff !important', // Force white text with high specificity
          maxHeight: '300px',
          overflowY: 'scroll',
        },
        arrow: {
          color: colors.$tooltipBgColor,
        },
      },
    },
  },
});